import {
  ChangeEvent,
  ClipboardEvent,
  DetailedHTMLProps,
  FormHTMLAttributes,
  JSX,
  KeyboardEvent,
  ReactNode,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import OtpSingleInput from './OtpSingleInput';

interface OtpInputProps extends Omit<
  DetailedHTMLProps<FormHTMLAttributes<HTMLFormElement>, HTMLFormElement>,
  'children' | 'onSubmit'
> {
  children?: (params: {
    renderInput(): JSX.Element;
    value: string;
  }) => ReactNode;
  disabled?: boolean;
  id?: string;
  isInvalid?: boolean;
  numberOfInputs: number;
  onSubmit?(value: string): void;
  // Make sure this is memoized
  onValueChange?(value: string): void;
  initialValue?: string;
}

interface OtpInputReducerState {
  focusIndex: number;
  numberOfInputs: number;
  value: string[];
}

function getInitState(
  numberOfInputs: number,
  initialValue?: string
): OtpInputReducerState {
  return {
    focusIndex: 0,
    numberOfInputs,
    value: initialValue ? initialValue.split('') : [],
  };
}

const FOCUS_INPUT = 'FOCUS_INPUT';

const FOCUS_NEXT_INPUT = 'FOCUS_NEXT_INPUT';

const FOCUS_PREVIOUS_INPUT = 'FOCUS_PREVIOUS_INPUT';

const CLEAR_FOCUS = 'CLEAR_FOCUS';

const ON_CHANGE = 'ON_CHANGE';

const ON_PASTE = 'ON_PASTE';

const RESET = 'RESET';

interface FocusInputAction {
  index: number;
  type: typeof FOCUS_INPUT;
}

interface FocusNextInputAction {
  type: typeof FOCUS_NEXT_INPUT;
}

interface FocusPreviousInputAction {
  type: typeof FOCUS_PREVIOUS_INPUT;
}

interface ClearFocusAction {
  type: typeof CLEAR_FOCUS;
}

interface OnChangeAction {
  index: number;
  type: typeof ON_CHANGE;
  value: string;
}

interface OnPasteAction {
  index: number;
  type: typeof ON_PASTE;
  value: string;
}

interface ResetAction {
  type: typeof RESET;
}

type OtpInputReducerAction =
  | ClearFocusAction
  | FocusInputAction
  | FocusNextInputAction
  | FocusPreviousInputAction
  | OnChangeAction
  | OnPasteAction
  | ResetAction;

function reducer(
  state: OtpInputReducerState,
  action: OtpInputReducerAction
): OtpInputReducerState {
  switch (action.type) {
    case FOCUS_INPUT:
      return { ...state, focusIndex: action.index };
    case FOCUS_NEXT_INPUT: {
      if (state.focusIndex + 1 < state.numberOfInputs) {
        return { ...state, focusIndex: state.focusIndex + 1 };
      }

      return { ...state };
    }
    case FOCUS_PREVIOUS_INPUT: {
      if (state.focusIndex > 0) {
        return { ...state, focusIndex: state.focusIndex - 1 };
      }

      return { ...state };
    }
    case CLEAR_FOCUS:
      return { ...state, focusIndex: -1 };
    case ON_CHANGE: {
      const value = [...state.value];
      let { focusIndex } = state;
      const newInputValue = action.value[0];

      value[action.index] = newInputValue;

      // If the user entered a value, jump to the next input
      if (newInputValue && state.focusIndex + 1 < state.numberOfInputs) {
        focusIndex = state.focusIndex + 1;
      }

      return { ...state, focusIndex, value };
    }
    case ON_PASTE: {
      const value = [...state.value];
      const pastedChars = action.value.split('');

      for (let pos = 0; pos < state.numberOfInputs; pos += 1) {
        if (pos >= action.index && pastedChars.length > 0) {
          value[pos] = pastedChars.shift() as string;
        }
      }

      return { ...state, value };
    }
    case RESET:
      return { ...state, focusIndex: 0, value: [] };
    default:
      return { ...state, value: [...state.value] };
  }
}

// https://github.com/devfolioco/react-otp-input
function OtpInput({
  children,
  disabled,
  id,
  initialValue,
  isInvalid,
  numberOfInputs,
  onSubmit,
  onValueChange,
}: OtpInputProps): JSX.Element {
  const [{ focusIndex, value }, dispatch] = useReducer(
    reducer,
    getInitState(numberOfInputs, initialValue)
  );
  const joinedValue = useMemo(() => value.join(''), [value]);

  function onPaste(e: ClipboardEvent<HTMLDivElement>, index: number): void {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData('text/plain')
      .slice(0, numberOfInputs - index);

    dispatch({ type: ON_PASTE, value: pasted ?? '', index });
  }

  function renderInput() {
    return (
      <div className="flex flex-row justify-center space-x-2">
        {Array(numberOfInputs)
          .fill(0)
          .map((_v, index) => (
            <OtpSingleInput
              disabled={disabled}
              focus={focusIndex === index}
              key={index}
              onBlur={(): void => dispatch({ type: CLEAR_FOCUS })}
              onChange={(
                e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
              ): void =>
                dispatch({
                  type: ON_CHANGE,
                  index,
                  value: e.target.value ?? '',
                })
              }
              onFocus={(e): void => {
                dispatch({ type: FOCUS_INPUT, index });
                e.target.select();
              }}
              onKeyDown={(
                e: KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>
              ): void => {
                if (e.key === 'Backspace') {
                  e.preventDefault();
                  dispatch({ type: ON_CHANGE, index, value: '' });
                  dispatch({ type: FOCUS_PREVIOUS_INPUT });
                } else if (e.key === 'Delete') {
                  e.preventDefault();
                  dispatch({ type: ON_CHANGE, index, value: '' });
                } else if (e.key === 'ArrowLeft') {
                  e.preventDefault();
                  dispatch({ type: FOCUS_PREVIOUS_INPUT });
                } else if (e.key === 'ArrowRight' || e.key === 'Tab') {
                  e.preventDefault();
                  dispatch({ type: FOCUS_NEXT_INPUT });
                } else if (
                  e.key === ' ' ||
                  e.key === 'Spacebar' ||
                  e.key === 'Space'
                ) {
                  e.preventDefault();
                }
              }}
              onPaste={(e): void => onPaste(e, index)}
              value={value[index] ?? ''}
            />
          ))}
      </div>
    );
  }

  function renderContent() {
    if (!children) {
      return renderInput();
    }

    return children({ renderInput, value: joinedValue });
  }

  useEffect(() => {
    if (onValueChange) {
      onValueChange(joinedValue);
    }
  }, [onValueChange, joinedValue]);

  useEffect(() => {
    if (isInvalid) {
      dispatch({ type: RESET });
    }
  }, [isInvalid]);

  return (
    <form
      id={id}
      onSubmit={
        onSubmit
          ? (e): void => {
              e.preventDefault();
              onSubmit(joinedValue);
            }
          : undefined
      }
    >
      {renderContent()}
    </form>
  );
}

export default OtpInput;
